import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { ArrowDownAZ, ArrowUpAZ, ChevronDown, Funnel, Plus, X } from 'lucide-react'
import { useState } from 'react'

import {
  hasValueList,
  KIND_OPERATORS,
  needsValue,
  normalizeFilter,
  type ColumnFilter,
  type FilterCondition,
  type FilterFieldKind,
  VALUE_LIST_LIMIT,
} from '@/components/filters/column-filter'
import { DateInput } from '@/components/filters/DateInput'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { messages } from '@/lib/messages'

const t = messages.filters.column

type ValuesFilter = Extract<ColumnFilter, { type: 'values' }>
type ConditionsFilter = Extract<ColumnFilter, { type: 'conditions' }>

export interface ColumnSortControl {
  direction: false | 'asc' | 'desc'
  /** Sütunu sonraki öncelik olarak ekler; zaten sıralıysa yalnızca yönü değişir. */
  onSort: (desc: boolean) => void
  onRemove: () => void
}

interface ColumnFilterPopoverProps {
  label: string
  kind: FilterFieldKind
  filter?: ColumnFilter
  /** `undefined`: filtreyi kaldır. */
  onApply: (filter: ColumnFilter | undefined) => void
  /** Değer listesi sorgusu (arama metniyle); yalnızca metin sütunlarında kullanılır. */
  values?: (search: string) => UseQueryOptions<string[]>
  /** Sıralanamayan sütunda verilmez. */
  sort?: ColumnSortControl
}

/** Excel'deki gibi başlık hunisi: sıralama + değer listesi / koşullu filtre. */
export function ColumnFilterPopover({
  label,
  kind,
  filter,
  onApply,
  values,
  sort,
}: ColumnFilterPopoverProps) {
  const [open, setOpen] = useState(false)
  const active = filter !== undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        // uiarc veri ızgarası başlık menüsü düğmesi: 24px, sütun adının yanında.
        render={
          <button
            type="button"
            className="text-text-muted hover:bg-surface-muted hover:text-foreground data-popup-open:bg-surface-muted data-popup-open:text-foreground ease-standard grid size-6 shrink-0 cursor-pointer place-items-center rounded-lg transition-colors duration-(--duration-fast) motion-reduce:transition-none"
            aria-label={t.open(label)}
            aria-pressed={active}
            title={t.open(label)}
          />
        }
      >
        {active ? (
          <Funnel className="text-brand size-3.5 fill-current" />
        ) : (
          <ChevronDown className="size-3.5" />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        // Yer darsa popover yukarı çevriliyordu. Dev üst dolgu üst tarafı hep "daha kötü" yapar,
        // flip aşağıda kalır; yatay kaydırma (sağ kenardaki sütun) çalışmaya devam eder.
        // Bu dolgu kullanılabilir yüksekliği bozar; yükseklik sabit sınırlı.
        collisionPadding={{ top: 10000, right: 8, bottom: 8, left: 8 }}
        className="flex max-h-[min(32rem,calc(100dvh-4rem))] w-80 flex-col gap-3 overflow-y-auto"
        // Portal olsa da React olayları başlığa kabarcıklanır: sürüklemeyi başlatmasın.
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {sort && (
          <>
            <ToggleGroup
              variant="outline"
              size="sm"
              value={sort.direction ? [sort.direction] : []}
              onValueChange={([value]) => {
                if (value) sort.onSort(value === 'desc')
                else sort.onRemove()
                setOpen(false)
              }}
            >
              <ToggleGroupItem value="asc" title={t.sortAscHint}>
                <ArrowDownAZ />
                {t.sortAsc}
              </ToggleGroupItem>
              <ToggleGroupItem value="desc" title={t.sortDescHint}>
                <ArrowUpAZ />
                {t.sortDesc}
              </ToggleGroupItem>
            </ToggleGroup>
            <Separator />
          </>
        )}
        <FilterEditor
          kind={kind}
          filter={filter}
          values={values}
          onApply={(next) => {
            onApply(next)
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

/** Taslak burada: popover her açılışta uygulanmış filtreden yeniden kurulur. */
function FilterEditor({
  kind,
  filter,
  values,
  onApply,
}: Pick<ColumnFilterPopoverProps, 'kind' | 'filter' | 'values' | 'onApply'>) {
  const withValues = hasValueList(kind)
  const withConditions = KIND_OPERATORS[kind].length > 0
  const [tab, setTab] = useState(filter?.type ?? (withValues ? 'values' : 'conditions'))
  const [valueDraft, setValueDraft] = useState<ValuesFilter>(
    filter?.type === 'values' ? filter : { type: 'values', mode: 'out', values: [], blank: true },
  )
  const [conditionDraft, setConditionDraft] = useState<ConditionsFilter>(
    filter?.type === 'conditions'
      ? filter
      : { type: 'conditions', join: 'and', conditions: [emptyCondition(kind)] },
  )

  const draft = tab === 'values' ? valueDraft : conditionDraft
  const nothingSelected =
    draft.type === 'values' && draft.mode === 'in' && draft.values.length === 0 && !draft.blank

  const valuesPanel = (
    <ValueList kind={kind} draft={valueDraft} onChange={setValueDraft} values={values} />
  )
  const conditionsPanel = (
    <ConditionList kind={kind} draft={conditionDraft} onChange={setConditionDraft} />
  )

  return (
    <>
      {withValues && withConditions ? (
        <Tabs value={tab} onValueChange={(value) => setTab(value as ColumnFilter['type'])}>
          <TabsList className="w-full">
            <TabsTrigger value="values">{t.values}</TabsTrigger>
            <TabsTrigger value="conditions">{t.conditions}</TabsTrigger>
          </TabsList>
          <TabsContent value="values">{valuesPanel}</TabsContent>
          <TabsContent value="conditions">{conditionsPanel}</TabsContent>
        </Tabs>
      ) : withValues ? (
        valuesPanel
      ) : (
        conditionsPanel
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" disabled={!filter} onClick={() => onApply(undefined)}>
          {t.clear}
        </Button>
        <Button
          size="sm"
          disabled={nothingSelected}
          onClick={() => onApply(normalizeFilter(draft, kind))}
        >
          {t.apply}
        </Button>
      </div>
    </>
  )
}

const BOOL_OPTIONS = ['true', 'false']
const valueLabel = (kind: FilterFieldKind, value: string) =>
  kind === 'bool' ? (value === 'true' ? messages.common.yes : messages.common.no) : value

function ValueList({
  kind,
  draft,
  onChange,
  values,
}: {
  kind: FilterFieldKind
  draft: ValuesFilter
  onChange: (draft: ValuesFilter) => void
  values?: ColumnFilterPopoverProps['values']
}) {
  const [search, setSearch] = useState('')
  const debounced = useDebouncedValue(search.trim(), 250)
  const isBool = kind === 'bool'
  const query = useQuery({
    ...(values?.(debounced) ?? { queryKey: ['column-filter', 'none'], queryFn: () => [] }),
    enabled: !isBool && values !== undefined,
    placeholderData: keepPreviousData,
  })
  const options = isBool ? BOOL_OPTIONS : (query.data ?? [])

  const isChecked = (value: string) => (draft.mode === 'in') === draft.values.includes(value)
  const setChecked = (targets: string[], checked: boolean) => {
    const listed = new Set(draft.values)
    // `in`: liste = işaretliler; `out`: liste = işaretsizler.
    const keep = (draft.mode === 'in') === checked
    for (const value of targets) {
      if (keep) listed.add(value)
      else listed.delete(value)
    }
    onChange({ ...draft, values: [...listed] })
  }

  const searching = debounced !== ''
  const allChecked = searching
    ? options.length > 0 && options.every(isChecked)
    : draft.mode === 'out' && draft.values.length === 0 && draft.blank
  const toggleAll = () => {
    if (searching) setChecked(options, !allChecked)
    else
      onChange({ type: 'values', mode: allChecked ? 'in' : 'out', values: [], blank: !allChecked })
  }

  return (
    <Command shouldFilter={false} className="border-border rounded-md border">
      {!isBool && (
        <CommandInput value={search} onValueChange={setSearch} placeholder={t.searchValues} />
      )}
      <CommandList className="max-h-60">
        <CommandEmpty>{query.isFetching ? t.loadingValues : t.noValues}</CommandEmpty>
        <CommandGroup>
          <CommandItem value="__all" onSelect={toggleAll}>
            <Checkbox checked={allChecked} tabIndex={-1} />
            {searching ? t.selectAllResults : t.selectAll}
          </CommandItem>
          {!searching && (
            <CommandItem
              value="__blank"
              onSelect={() => onChange({ ...draft, blank: !draft.blank })}
            >
              <Checkbox checked={draft.blank} tabIndex={-1} />
              {t.blank}
            </CommandItem>
          )}
        </CommandGroup>
        {options.length > 0 && <CommandSeparator />}
        <CommandGroup>
          {options.map((value) => (
            <CommandItem
              key={value}
              value={value}
              onSelect={() => setChecked([value], !isChecked(value))}
            >
              <Checkbox checked={isChecked(value)} tabIndex={-1} />
              <span className="truncate">{valueLabel(kind, value)}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      {options.length >= VALUE_LIST_LIMIT && (
        <p className="text-muted-foreground border-border border-t px-2 py-1.5 text-xs">
          {t.moreValues}
        </p>
      )}
    </Command>
  )
}

const emptyCondition = (kind: FilterFieldKind): FilterCondition => ({
  op: kind === 'text' ? 'contains' : 'eq',
  value: '',
})

function inputType(kind: FilterFieldKind) {
  if (kind === 'numeric') return 'number'
  if (kind === 'date' || kind === 'datetime') return 'date'
  return 'text'
}

/** Koşul değeri: tarih türlerinde `DateInput` (yerel tarih girişi değil), diğerlerinde `Input`; satırdaki
 * işleç seçicisiyle aynı yükseklik (`--control-height-sm`). */
function ValueInput({
  type,
  value,
  onChange,
  'aria-label': ariaLabel,
}: {
  type: ReturnType<typeof inputType>
  value: string
  onChange: (value: string) => void
  'aria-label': string
}) {
  if (type === 'date') {
    return (
      <DateInput
        className="h-(--control-height-sm)"
        aria-label={ariaLabel}
        value={value}
        onChange={onChange}
      />
    )
  }
  return (
    <Input
      type={type}
      className="h-(--control-height-sm)"
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

function ConditionList({
  kind,
  draft,
  onChange,
}: {
  kind: FilterFieldKind
  draft: ConditionsFilter
  onChange: (draft: ConditionsFilter) => void
}) {
  const update = (index: number, patch: Partial<FilterCondition>) =>
    onChange({
      ...draft,
      conditions: draft.conditions.map((condition, i) =>
        i === index ? { ...condition, ...patch } : condition,
      ),
    })
  const remove = (index: number) =>
    onChange({ ...draft, conditions: draft.conditions.filter((_, i) => i !== index) })
  const type = inputType(kind)
  const operatorItems = KIND_OPERATORS[kind].map((op) => ({ value: op, label: t.operators[op] }))

  return (
    <FieldGroup className="gap-3">
      {draft.conditions.length > 1 && (
        <Field orientation="horizontal">
          <FieldLabel>{t.join}</FieldLabel>
          <ToggleGroup
            variant="outline"
            size="sm"
            value={[draft.join]}
            onValueChange={([value]) => {
              if (value) onChange({ ...draft, join: value as ConditionsFilter['join'] })
            }}
          >
            <ToggleGroupItem value="and">{t.and}</ToggleGroupItem>
            <ToggleGroupItem value="or">{t.or}</ToggleGroupItem>
          </ToggleGroup>
        </Field>
      )}
      {draft.conditions.map((condition, index) => (
        <Field key={index} className="gap-1.5">
          <div className="flex items-center gap-1">
            <Select
              value={condition.op}
              items={operatorItems}
              onValueChange={(op) => update(index, { op: op as FilterCondition['op'] })}
            >
              <SelectTrigger size="sm" className="flex-1" aria-label={t.operator}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {KIND_OPERATORS[kind].map((op) => (
                    <SelectItem key={op} value={op}>
                      {t.operators[op]}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t.removeCondition}
              disabled={draft.conditions.length === 1}
              onClick={() => remove(index)}
            >
              <X />
            </Button>
          </div>
          {needsValue(condition.op) && (
            <div className="flex items-center gap-1">
              <ValueInput
                type={type}
                aria-label={t.value}
                value={condition.value}
                onChange={(value) => update(index, { value })}
              />
              {condition.op === 'between' && (
                <ValueInput
                  type={type}
                  aria-label={t.valueTo}
                  value={condition.valueTo ?? ''}
                  onChange={(valueTo) => update(index, { valueTo })}
                />
              )}
            </div>
          )}
        </Field>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="self-start"
        onClick={() =>
          onChange({ ...draft, conditions: [...draft.conditions, emptyCondition(kind)] })
        }
      >
        <Plus data-icon="inline-start" />
        {t.addCondition}
      </Button>
    </FieldGroup>
  )
}
